export type RateLimitedEvent = {
  path: string
  retryAfterSeconds: number | null
}

type RateLimitedListener = (event: RateLimitedEvent) => void

const listeners = new Set<RateLimitedListener>()
let cooldownUntil = 0

export function rateLimitRetryAfterSeconds(error: unknown): number | null | undefined {
  if (error === null || typeof error !== 'object') {
    return undefined
  }

  const candidate = error as { status?: unknown; retryAfterSeconds?: unknown }
  if (candidate.status !== 429) {
    return undefined
  }

  return typeof candidate.retryAfterSeconds === 'number' && Number.isFinite(candidate.retryAfterSeconds)
    ? Math.max(0, Math.ceil(candidate.retryAfterSeconds))
    : null
}

export function isRateLimitedError(error: unknown): boolean {
  return rateLimitRetryAfterSeconds(error) !== undefined
}

export function backoffMs(error: unknown, failureCount = 0): number {
  const retryAfter = rateLimitRetryAfterSeconds(error)
  const base = retryAfter !== undefined && retryAfter !== null
    ? retryAfter * 1000
    : Math.min(1000 * 2 ** Math.max(0, failureCount), 30_000)

  return base + Math.floor(Math.random() * Math.min(1000, Math.max(1, base * 0.1)))
}

export function emitRateLimited(event: RateLimitedEvent): void {
  const seconds = event.retryAfterSeconds ?? 30
  cooldownUntil = Math.max(cooldownUntil, Date.now() + Math.max(1, seconds) * 1000)
  listeners.forEach((listener) => listener(event))
}

export function onRateLimited(listener: RateLimitedListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function isRateLimitCooldownActive(): boolean {
  return cooldownUntil > Date.now()
}

export function rateLimitCooldownRemainingSeconds(): number {
  return Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000))
}

export function pollInterval(baseMs: number, error?: unknown): number {
  const retryAfter = rateLimitRetryAfterSeconds(error)
  const errorDelay = retryAfter !== undefined && retryAfter !== null ? retryAfter * 1000 : 0
  const cooldownDelay = Math.max(0, cooldownUntil - Date.now())

  return Math.max(baseMs, errorDelay, cooldownDelay)
}
